// src/pages/Reports.tsx
import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Users, 
  Shield, 
  DollarSign, 
  FileText, 
  AlertTriangle,
  ClipboardList,
  TrendingUp 
} from 'lucide-react';
import ReportCard from '../components/reports/ReportCard';
import CategoryFilter from '../components/reports/CategoryFilter';
import DateRangePicker from '../components/reports/DateRangePicker';
import ReportResultsModal from '../components/reports/ReportResultsModal';
import reportsAPI from '../services/reportsAPI';
import { categoryAPI } from '../services/categoryAPI';

interface CategoryOption {
  categoryId: string;
  name: string;
  parentCategoryId?: string;
}

interface OfficerOption {
  userId: string;
  firstName: string;
  lastName: string;
  badgeNumber: string;
  rank: string;
}

const Reports: React.FC = () => {
  // State for filters and data
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [officers, setOfficers] = useState<OfficerOption[]>([]);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  
const [reportResults, setReportResults] = useState<{
  isOpen: boolean;
  title: string;
  type: string;
  data: any;
}>({
  isOpen: false,
  title: '',
  type: '',
  data: null
});
  // Filter states for different reports
  const [inventoryFilters, setInventoryFilters] = useState({
    categoryId: '',
    subcategoryId: '',
    includeAssigned: true,
  });
  
  const [budgetFilters, setBudgetFilters] = useState({
    startDate: '',
    endDate: '',
    compareWithPrevious: true,
  });
  
  const [officerFilters, setOfficerFilters] = useState({
    officerId: '',
    status: 'Active',
  });
  
  const [auditFilters, setAuditFilters] = useState({
    reportType: 'FIREARMS' as 'FIREARMS' | 'SERIALIZED_ALL' | 'BODY_ARMOR',
    includeHistory: false,
  });
  
  const [expirationFilters, setExpirationFilters] = useState({
    daysAhead: 180,
    expirationTypes: ['BODY_ARMOR'],
  });

  // Load initial data
  useEffect(() => {
    loadCategories();
    loadOfficers();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      
      if (response.success && response.data?.categories) {
        // getAllCategories returns a flat list - just map to CategoryOption
        const flatCategories: CategoryOption[] = response.data.categories.map(cat => ({
          categoryId: cat.categoryId,
          name: cat.name,
          parentCategoryId: cat.parentCategoryId ?? undefined
        }));
        
        setCategories(flatCategories);
      } else {
        console.error('Failed to load categories:', response.error);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    }
  };

  const loadOfficers = async () => {
    try {
      const response = await reportsAPI.getOfficersList();
      setOfficers(response.users || []);
    } catch (error) {
      console.error('Error loading officers:', error);
      setOfficers([]);
    }
  };

  const setReportLoading = (reportType: string, isLoading: boolean) => {
    setLoading(prev => ({ ...prev, [reportType]: isLoading }));
  };

  const handleDownload = async (reportType: string, format: 'PDF' | 'EXCEL', filters: any) => {
    try {
      setReportLoading(`${reportType}_${format}`, true);
      
      const blob = await reportsAPI.downloadReport({
        type: reportType,
        format,
        filters,
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}_${new Date().toISOString().split('T')[0]}.${format.toLowerCase()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error(`Error downloading ${reportType} ${format}:`, error);
      alert(`Error downloading report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setReportLoading(`${reportType}_${format}`, false);
    }
  };

  const handleGenerateReport = async (reportType: string, filters: any) => {
  try {
    setReportLoading(reportType, true);
    
    let data;
    let title;
    
    switch (reportType) {
      case 'INVENTORY_WORKSHEET':
        data = await reportsAPI.generateInventoryWorksheet(filters);
        title = 'Physical Inventory Worksheet';
        break;
      case 'OFFICER_EQUIPMENT':
        data = await reportsAPI.generateOfficerEquipmentReport(filters);
        title = 'Officer Equipment Report';
        break;
      case 'BUDGET_ANALYSIS':
  data = await reportsAPI.generateBudgetAnalysisReport(filters);
  title = 'Budget Analysis Report';
  break;
case 'ANNUAL_ASSIGNMENTS':
  const assignmentFilters = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    compareWithPreviousPeriod: filters.compareWithPrevious
  };
  data = await reportsAPI.generateAnnualAssignmentReport(assignmentFilters);
  title = 'Annual Assignment Report';
  break;
case 'EQUIPMENT_AUDIT':
        data = await reportsAPI.generateEquipmentAuditReport(filters);
        title = 'Equipment Audit Report';
        break;
      case 'EXPIRATION_REPORT':
  data = await reportsAPI.generateExpirationReport(filters);
  title = 'Equipment Expiration Report';
  break;
case 'BODY_ARMOR_STATUS':
  // Map to expiration report with body armor focus
  const bodyArmorFilters = {
    daysAhead: 365,
    expirationTypes: ['BODY_ARMOR']
  };
  data = await reportsAPI.generateExpirationReport(bodyArmorFilters);
  title = 'Body Armor Status Report';
  break;
default:
  throw new Error('Unknown report type');
    }
    
    setReportResults({
      isOpen: true,
      title,
      type: reportType,
      data
    });
    
  } catch (error) {
    console.error(`Error generating ${reportType}:`, error);
    alert(`Error generating report: ${error instanceof Error ? error.message : 'Unknown error'}`);
  } finally {
    setReportLoading(reportType, false);
  }
};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
        <p className="mt-1 text-sm text-gray-600">
          Generate detailed reports for inventory management, compliance, and budget planning.
        </p>
      </div>

      {/* Inventory Reports Section */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Package className="h-5 w-5 mr-2 text-blue-600" />
            Inventory Reports
          </h3>
          
          <div className="grid gap-6">
            {/* Current Inventory Worksheet */}
            <ReportCard
              title="Physical Inventory Worksheet"
              description="Printable worksheet for conducting physical inventory counts with checkboxes for serialized items and quantity fields for bulk items."
              icon={<ClipboardList className="h-6 w-6" />}
              onGenerate={() => handleGenerateReport('INVENTORY_WORKSHEET', inventoryFilters)}
              onDownloadPDF={() => handleDownload('INVENTORY_WORKSHEET', 'PDF', inventoryFilters)}
              onDownloadExcel={() => handleDownload('INVENTORY_WORKSHEET', 'EXCEL', inventoryFilters)}
              isLoading={loading.INVENTORY_WORKSHEET}
            >
              <CategoryFilter
                categories={categories}
                selectedCategoryId={inventoryFilters.categoryId}
                selectedSubcategoryId={inventoryFilters.subcategoryId}
                onCategoryChange={(categoryId) => 
                  setInventoryFilters(prev => ({ ...prev, categoryId, subcategoryId: '' }))
                }
                onSubcategoryChange={(subcategoryId) => 
                  setInventoryFilters(prev => ({ ...prev, subcategoryId }))
                }
              />
              
              <div className="mt-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={inventoryFilters.includeAssigned}
                    onChange={(e) => 
                      setInventoryFilters(prev => ({ ...prev, includeAssigned: e.target.checked }))
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Include assigned items</span>
                </label>
              </div>
            </ReportCard>

            {/* Annual Assignment Report */}
            <ReportCard
              title="Annual Assignment Report"
              description="Equipment distributed during a specified time period. Shows assignment trends and helps with budget planning."
              icon={<TrendingUp className="h-6 w-6" />}
              onGenerate={() => handleGenerateReport('ANNUAL_ASSIGNMENTS', budgetFilters)}
              onDownloadPDF={() => handleDownload('ANNUAL_ASSIGNMENTS', 'PDF', budgetFilters)}
              onDownloadExcel={() => handleDownload('ANNUAL_ASSIGNMENTS', 'EXCEL', budgetFilters)}
              isLoading={loading.ANNUAL_ASSIGNMENTS}
            >
              <DateRangePicker
                startDate={budgetFilters.startDate}
                endDate={budgetFilters.endDate}
                onStartDateChange={(date) => 
                  setBudgetFilters(prev => ({ ...prev, startDate: date }))
                }
                onEndDateChange={(date) => 
                  setBudgetFilters(prev => ({ ...prev, endDate: date }))
                }
                label="Assignment Period"
              />
            </ReportCard>
          </div>
        </div>
      </div>

      {/* Financial Reports Section */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-green-600" />
            Financial Reports
          </h3>
          
          <div className="grid gap-6">
            {/* Budget Analysis Report */}
            <ReportCard
              title="Budget Analysis Report"
              description="Annual spending by category with 2-year trend comparison. Essential for budget planning and chief briefings."
              icon={<TrendingUp className="h-6 w-6" />}
              onGenerate={() => handleGenerateReport('BUDGET_ANALYSIS', budgetFilters)}
              onDownloadPDF={() => handleDownload('BUDGET_ANALYSIS', 'PDF', budgetFilters)}
              onDownloadExcel={() => handleDownload('BUDGET_ANALYSIS', 'EXCEL', budgetFilters)}
              isLoading={loading.BUDGET_ANALYSIS}
            >
              <DateRangePicker
                startDate={budgetFilters.startDate}
                endDate={budgetFilters.endDate}
                onStartDateChange={(date) => 
                  setBudgetFilters(prev => ({ ...prev, startDate: date }))
                }
                onEndDateChange={(date) => 
                  setBudgetFilters(prev => ({ ...prev, endDate: date }))
                }
                label="Budget Period"
              />
              
              <div className="mt-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={budgetFilters.compareWithPrevious}
                    onChange={(e) => 
                      setBudgetFilters(prev => ({ ...prev, compareWithPrevious: e.target.checked }))
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Compare with previous period</span>
                </label>
              </div>
            </ReportCard>
          </div>
        </div>
      </div>

      {/* Officer Reports Section */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-purple-600" />
            Officer Reports
          </h3>
          
          <div className="grid gap-6">
            {/* Officer Equipment Report */}
            <ReportCard
              title="Officer Equipment Report"
              description="Current equipment assignments by officer. Shows what each officer currently has assigned."
              icon={<Users className="h-6 w-6" />}
              onGenerate={() => handleGenerateReport('OFFICER_EQUIPMENT', officerFilters)}
              onDownloadPDF={() => handleDownload('OFFICER_EQUIPMENT', 'PDF', officerFilters)}
              onDownloadExcel={() => handleDownload('OFFICER_EQUIPMENT', 'EXCEL', officerFilters)}
              isLoading={loading.OFFICER_EQUIPMENT}
            >
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Officer (Leave blank for all officers)
                  </label>
                  <select
                    value={officerFilters.officerId}
                    onChange={(e) => 
                      setOfficerFilters(prev => ({ ...prev, officerId: e.target.value }))
                    }
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">All Officers</option>
                    {officers.map((officer) => (
                      <option key={officer.userId} value={officer.userId}>
                        {officer.firstName} {officer.lastName} (Badge #{officer.badgeNumber})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assignment Status
                  </label>
                  <select
                    value={officerFilters.status}
                    onChange={(e) => 
                      setOfficerFilters(prev => ({ ...prev, status: e.target.value }))
                    }
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="Active">Active Assignments Only</option>
                    <option value="All">All Assignments (including returned)</option>
                  </select>
                </div>
              </div>
            </ReportCard>
          </div>
        </div>
      </div>

      {/* Compliance Reports Section */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Shield className="h-5 w-5 mr-2 text-red-600" />
            Compliance & Audit Reports
          </h3>
          
          <div className="grid gap-6">
            {/* Equipment Audit Report */}
            <ReportCard
              title="Equipment Audit Report"
              description="Complete audit trail for serialized equipment. Includes serial numbers, assignments, and location history."
              icon={<FileText className="h-6 w-6" />}
              onGenerate={() => handleGenerateReport('EQUIPMENT_AUDIT', auditFilters)}
              onDownloadPDF={() => handleDownload('EQUIPMENT_AUDIT', 'PDF', auditFilters)}
              onDownloadExcel={() => handleDownload('EQUIPMENT_AUDIT', 'EXCEL', auditFilters)}
              isLoading={loading.EQUIPMENT_AUDIT}
            >
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Report Type
                  </label>
                  <select
                    value={auditFilters.reportType}
                    onChange={(e) => 
                      setAuditFilters(prev => ({ 
                        ...prev, 
                        reportType: e.target.value as 'FIREARMS' | 'SERIALIZED_ALL' | 'BODY_ARMOR' 
                      }))
                    }
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="FIREARMS">Firearms Only</option>
                    <option value="SERIALIZED_ALL">All Serialized Equipment</option>
                    <option value="BODY_ARMOR">Body Armor Only</option>
                  </select>
                </div>
                
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={auditFilters.includeHistory}
                      onChange={(e) => 
                        setAuditFilters(prev => ({ ...prev, includeHistory: e.target.checked }))
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Include assignment history</span>
                  </label>
                </div>
              </div>
            </ReportCard>

            {/* Body Armor Status Report */}
            <ReportCard
              title="Body Armor Status Report"
              description="All body armor with expiration dates and assignment status. Critical for officer safety compliance."
              icon={<Shield className="h-6 w-6" />}
              onGenerate={() => handleGenerateReport('BODY_ARMOR_STATUS', {})}
              onDownloadPDF={() => handleDownload('BODY_ARMOR_STATUS', 'PDF', {})}
              onDownloadExcel={() => handleDownload('BODY_ARMOR_STATUS', 'EXCEL', {})}
              isLoading={loading.BODY_ARMOR_STATUS}
            >
              <p className="text-sm text-gray-600">
                This report includes all body armor items with expiration dates, current assignments, 
                and highlights items that are expired or expiring soon.
              </p>
            </ReportCard>

            {/* Expiring Equipment Report */}
            <ReportCard
              title="Expiring Equipment Report"
              description="Equipment requiring replacement or renewal. Configurable warning periods for proactive planning."
              icon={<AlertTriangle className="h-6 w-6" />}
              onGenerate={() => handleGenerateReport('EXPIRATION_REPORT', expirationFilters)}
              onDownloadPDF={() => handleDownload('EXPIRATION_REPORT', 'PDF', expirationFilters)}
              onDownloadExcel={() => handleDownload('EXPIRATION_REPORT', 'EXCEL', expirationFilters)}
              isLoading={loading.EXPIRATION_REPORT}
            >
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Look-ahead Period (days)
                  </label>
                  <select
                    value={expirationFilters.daysAhead}
                    onChange={(e) => 
                      setExpirationFilters(prev => ({ ...prev, daysAhead: parseInt(e.target.value) }))
                    }
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                    <option value={180}>6 months</option>
                    <option value={365}>1 year</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiration Types
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'BODY_ARMOR', label: 'Body Armor' },
                      { value: 'CERTIFICATION', label: 'Certifications' },
                      { value: 'WARRANTY', label: 'Warranties' },
                      { value: 'OTHER', label: 'Other' },
                    ].map((type) => (
                      <label key={type.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={expirationFilters.expirationTypes.includes(type.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setExpirationFilters(prev => ({
                                ...prev,
                                expirationTypes: [...prev.expirationTypes, type.value]
                              }));
                            } else {
                              setExpirationFilters(prev => ({
                                ...prev,
                                expirationTypes: prev.expirationTypes.filter(t => t !== type.value)
                              }));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </ReportCard>
          </div>
        </div>
      </div>
    {/* Report Results Modal */}
      <ReportResultsModal
        isOpen={reportResults.isOpen}
        onClose={() => setReportResults(prev => ({ ...prev, isOpen: false }))}
        title={reportResults.title}
        reportType={reportResults.type}
        data={reportResults.data}
        onDownloadPDF={() => handleDownload(reportResults.type, 'PDF', {})}
        onDownloadExcel={() => handleDownload(reportResults.type, 'EXCEL', {})}
        isDownloading={loading[`${reportResults.type}_PDF`] || loading[`${reportResults.type}_EXCEL`]}
      />
    </div>
  );
};

export default Reports;
